import { IdCard, User } from "lucide-react";
import Input from "@/components/ui/Input";
import DatePicker from "@/components/ui/DatePicker";
import UploadBlock from "./UploadBlock";
import { PERSONAL_DOCS } from "../data";
import type { FormErrors, KycForm, SetDoc, SetField } from "../types";

interface Props {
  form: KycForm;
  errors: FormErrors;
  setField: SetField;
  setDoc: SetDoc;
  retailerId?: string;
}

function formatAadhaar(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

export default function PersonalDocumentsStep({ form, errors, setField, setDoc, retailerId }: Props) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Personal Documents</h3>
      <p className="text-[13px] text-ink-secondary">
        Documents of the business owner or authorised signatory
      </p>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <Input
          label="Owner Full Name"
          required
          error={errors.ownerFullName}
          helper="As printed on the PAN card"
          placeholder="Enter full name"
          id="ownerFullName"
          prefixIcon={<User className="h-4 w-4" />}
          value={form.ownerFullName}
          onChange={(e) => setField("ownerFullName", e.target.value)}
        />
        <DatePicker
          label="Date of Birth"
          required
          error={errors.dateOfBirth}
          id="dateOfBirth"
          value={form.dateOfBirth}
          onChange={(v) => setField("dateOfBirth", v)}
        />
        <Input
          label="Personal PAN"
          required
          error={errors.personalPan}
          helper="Format: ABCDE1234F"
          placeholder="Format: ABCDE1234F"
          id="personalPan"
          maxLength={10}
          value={form.personalPan}
          onChange={(e) => setField("personalPan", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
        />
        <Input
          label="Aadhaar Number"
          required
          error={errors.aadhaarNumber}
          placeholder="XXXX XXXX XXXX"
          id="aadhaarNumber"
          inputMode="numeric"
          maxLength={14}
          prefixIcon={<IdCard className="h-4 w-4" />}
          value={form.aadhaarNumber}
          onChange={(e) => setField("aadhaarNumber", formatAadhaar(e.target.value))}
        />
      </div>

      <div className="mt-6 flex flex-col gap-4">
        {PERSONAL_DOCS.map((doc) => (
          <UploadBlock
            key={doc.key}
            label={doc.label}
            required
            helper={doc.helper}
            camera={doc.key === "selfie"}
            value={form.docs[doc.key] ?? null}
            onChange={(d) => setDoc(doc.key, d)}
            error={errors[`doc_${doc.key}`]}
            retailerId={retailerId}
          />
        ))}
      </div>
    </div>
  );
}